import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { SpeakUpLogo } from "@/components/Layout";

const DISMISS_KEY = "speakup_pwa_dismissed_at";
const DISMISS_TTL_DAYS = 14;

// NOTE: This dismissal timestamp is UX state, not sensitive data. It's used purely to
// avoid re-showing the install card to a user who already said "Not now". Persistence
// across sessions is the entire point of "dismiss for 14 days" — sessionStorage would
// reset on every tab close and defeat the feature. No PII, no tokens, no auth state
// is stored client-side here.
function isStandalone() {
    if (typeof window === "undefined") return false;
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    // iOS Safari sets navigator.standalone when installed
    return !!window.navigator?.standalone;
}

function isIos() {
    if (typeof navigator === "undefined") return false;
    return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios|edgios/i.test(navigator.userAgent);
}

function recentlyDismissed() {
    try {
        const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
        if (!at) return false;
        return Date.now() - at < DISMISS_TTL_DAYS * 24 * 3600 * 1000;
    } catch (err) {
        // localStorage can throw in private-mode Safari and some embedded webviews.
        console.warn("[pwa] localStorage read failed; assuming not dismissed:", err?.message || err);
        return false;
    }
}

/** Floating bottom install card. Listens for `beforeinstallprompt` (Chromium/Edge) and
 *  falls back to an iOS-Safari instruction sheet. Dismissal is remembered for 14 days. */
export default function PwaInstall() {
    const [deferred, setDeferred] = useState(null); // BeforeInstallPromptEvent
    const [show, setShow] = useState(false);
    const [iosTip, setIosTip] = useState(false);

    useEffect(() => {
        if (isStandalone()) return; // already installed

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
            if (!recentlyDismissed()) setShow(true);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);

        const onInstalled = () => {
            setShow(false);
            setDeferred(null);
        };
        window.addEventListener("appinstalled", onInstalled);

        // iOS Safari has no `beforeinstallprompt`. Show the tip after a small delay
        // so we don't compete with the initial paint.
        if (isIos() && !recentlyDismissed()) {
            const t = setTimeout(() => setShow(true), 1800);
            return () => {
                clearTimeout(t);
                window.removeEventListener("beforeinstallprompt", onPrompt);
                window.removeEventListener("appinstalled", onInstalled);
            };
        }

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch (err) {
            console.warn("[pwa] localStorage write failed (dismiss is in-memory only):", err?.message || err);
        }
        setShow(false);
        setIosTip(false);
    };

    const install = async () => {
        if (deferred) {
            try {
                deferred.prompt();
                await deferred.userChoice;
            } catch (err) {
                console.warn("[pwa] install prompt failed:", err?.message || err);
            }
            setDeferred(null);
            setShow(false);
            return;
        }
        if (isIos()) {
            setIosTip(true);
        }
    };

    if (!show) return null;

    return (
        <>
            <div
                className="fixed left-3 right-3 md:left-auto md:right-6 md:w-[360px] bottom-3 z-40 speakup-card p-4 flex items-center gap-3 shadow-lg"
                style={{ animation: "speakup-fadeup 0.45s ease-out both" }}
                data-testid="pwa-install-card"
            >
                <SpeakUpLogo size={40} />
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[var(--speakup-text-strong)] text-[14px] leading-tight">
                        Install SpeakUp
                    </div>
                    <div className="text-[12px] text-[var(--speakup-text-muted)] mt-0.5">
                        Add it to your home screen — opens like an app, no browser bar.
                    </div>
                </div>
                <button
                    onClick={install}
                    data-testid="pwa-install-btn"
                    className="h-10 px-3 rounded-[10px] text-white font-semibold text-[13px] inline-flex items-center gap-1.5 shrink-0"
                    style={{ background: "var(--speakup-red)" }}
                >
                    <Download size={14} /> Install
                </button>
                <button
                    onClick={dismiss}
                    data-testid="pwa-install-dismiss"
                    aria-label="Dismiss install prompt"
                    className="h-9 w-9 rounded-full flex items-center justify-center text-[var(--speakup-text-muted)] hover:bg-[var(--speakup-surface-muted)]"
                >
                    <X size={16} />
                </button>
            </div>

            {iosTip && (
                <div
                    className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center p-3"
                    onClick={() => setIosTip(false)}
                    data-testid="pwa-ios-tip"
                >
                    <div
                        className="speakup-card max-w-md w-full p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3">
                            <SpeakUpLogo size={32} />
                            <div className="display text-[18px]">Add to Home Screen</div>
                        </div>
                        <ol className="mt-4 space-y-2 text-[14px] list-decimal pl-5">
                            <li>
                                Tap the <strong>Share</strong> icon{" "}
                                <Share size={14} className="inline -mt-0.5" /> at the bottom of Safari.
                            </li>
                            <li>
                                Scroll and tap <strong>Add to Home Screen</strong>.
                            </li>
                            <li>
                                Tap <strong>Add</strong> in the top-right.
                            </li>
                        </ol>
                        <button
                            onClick={dismiss}
                            data-testid="pwa-ios-tip-close"
                            className="mt-5 h-11 w-full rounded-[10px] text-white font-semibold"
                            style={{ background: "var(--speakup-red)" }}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
